import type { Business, Sport } from "@teamna/shared";
import { useState, type FormEvent } from "react";
import {
  Button,
  ChipRow,
  ErrorState,
  Field,
  FormRow,
  FormSection,
  InlineError,
  SkeletonList,
  showToast,
} from "../../components/index.ts";
import { useOwnerBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { useUpdateBusiness } from "@teamna/web-client/hooks/use-setup.ts";
import { SPORT_OPTIONS } from "../../lib/sports.ts";
import { SaveBar, SetupFrame, WeekdayToggles } from "./frame.tsx";

/**
 * Setup step 1: name, sport, venue, days off (mobile spec 5.1 step 1). Settings > Business shows the same form.
 *
 * The step is done from the moment the business exists; this page edits it. Changing the sport
 * does not rename the business's words: those are set once at creation and live under Settings >
 * Words, so the preview line is not shown here.
 */
export function SetupBusinessScreen() {
  return <SetupFrame step="business">{(businessId) => <BusinessForm businessId={businessId} />}</SetupFrame>;
}

/** The business form, shared by setup step 1 and the Business tab of Settings. */
export function BusinessForm({ businessId }: { businessId: string }) {
  const business = useOwnerBusiness(businessId);
  if (business.isLoading) return <SkeletonList rows={3} />;
  if (business.error || !business.data) {
    return (
      <ErrorState error={business.error ?? new Error("missing")} onRetry={() => void business.refetch()} />
    );
  }
  return <BusinessFields business={business.data} />;
}

function BusinessFields({ business }: { business: Business }) {
  const update = useUpdateBusiness(business.id);
  const [name, setName] = useState(business.name);
  const [sport, setSport] = useState<Sport>(business.sport);
  const [venue, setVenue] = useState(business.venueName ?? "");
  const [daysOff, setDaysOff] = useState<number[]>(business.daysOff);
  const [error, setError] = useState<unknown>(null);

  const valid = name.trim().length >= 2;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!valid) return;
    setError(null);
    update.mutate(
      { name: name.trim(), sport, venueName: venue.trim() || null, daysOff },
      {
        onSuccess: () => showToast("Business saved"),
        onError: (caught) => setError(caught),
      },
    );
  }

  return (
    <form onSubmit={submit}>
      <FormSection title="Business" description="What people see at the top of every screen.">
        <FormRow label="Business name" hint="At least two letters.">
          <Field label="Business name" value={name} onChange={(event) => setName(event.target.value)} />
        </FormRow>
        <FormRow label="Sport" hint="Picks the starter groups and packages.">
          <ChipRow
            label="Sport"
            wrap
            options={SPORT_OPTIONS}
            selectedId={sport}
            onSelect={(id) => setSport(id as Sport)}
          />
          {sport !== business.sport ? (
            <p className="mt-2 text-[13px] text-ink-3">
              A new sport does not change the words your business uses. Change them in Settings, under Words.
            </p>
          ) : null}
        </FormRow>
        <FormRow label="Venue" hint="Optional.">
          <Field
            label="Venue"
            value={venue}
            placeholder="Al Nasr Sports Club"
            onChange={(event) => setVenue(event.target.value)}
          />
        </FormRow>
        <FormRow label="Days off" hint="The days you are closed. No sessions go on them.">
          <WeekdayToggles label="Days off" selected={daysOff} onChange={setDaysOff} />
        </FormRow>
        <FormRow label="Currency and language">
          <p className="pt-2 text-[14px] text-ink-2">AED, English</p>
        </FormRow>
      </FormSection>
      {error ? (
        <div className="mt-4">
          <InlineError error={error} />
        </div>
      ) : null}
      <SaveBar>
        <Button type="submit" disabled={!valid} loading={update.isPending}>
          Save
        </Button>
      </SaveBar>
    </form>
  );
}
